import React, { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FlaskConical, ArrowRight } from 'lucide-react';
import { WorldPageHeader } from '../../components/world/WorldPageHeader';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { ValidationLegend } from '../../components/progression/ValidationBadge';
import { ChecklistBar } from '../../components/progression/ChecklistBar';
import { ClassSelector } from '../../components/progression/evolution/ClassSelector';
import { ClassPath } from '../../components/progression/evolution/ClassPath';
import { SebinaSection } from '../../components/progression/evolution/SebinaSection';
import { MarlonSection } from '../../components/progression/evolution/MarlonSection';
import { ThirdEvolutionSection } from '../../components/progression/evolution/ThirdEvolutionSection';
import { getClass } from '../../data/guide/classes';
import { sebina, marlon, thirdEvolution } from '../../data/guide/evolutions';
import { useChecklist } from '../../hooks/useChecklist';
import { useWorldLookup } from '../../hooks/useWorldLookup';
import { useHashScroll } from '../../hooks/useHashScroll';
import type { CharacterClass, ClassId } from '../../types/progression';
import type { WorldLookup } from '../../utils/progressionLookup';
export function EvolutionsGuide() {
  const [params, setParams] = useSearchParams();
  const cls = getClass(params.get('clase'));
  const list = useChecklist(`evoluciones-${cls?.id ?? 'general'}`);
  const {
    status,
    lookup
  } = useWorldLookup();
  useHashScroll();
  useEffect(() => {
    document.title = `Evoluciones${cls ? ` · ${cls.base}` : ''} · Guía MU Vanguard`;
  }, [cls]);
  const keys = useMemo(() => cls ? progressKeys(cls, lookup) : [], [cls, lookup]);
  const done = keys.filter(k => list.isChecked(k)).length;
  const choose = (id: ClassId) => setParams({
    clase: id
  }, {
    replace: true
  });
  return <div className="space-y-8 md:space-y-12" data-id="element-2061" data-mp="2kd57yprsyemf">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Evoluciones'
    }]} eyebrow="Guía interactiva" title="Evoluciones de personaje" data-id="element-2062" data-mp="2ka96irgqx7t8">
        <p className="mt-2 max-w-2xl text-sm text-gray-300 md:text-base" data-id="element-2063" data-mp="1myk79qjhpr81">Elige tu clase y sigue solo el recorrido que le corresponde: qué necesitas, dónde conseguirlo y cómo completarlo.</p>
        <div className="mt-3" data-id="element-2064" data-mp="1vr490zv2wbx3">
          <CopyLinkButton data-id="element-2065" data-mp="1vrdqu05yzcrp" />
        </div>
      </WorldPageHeader>

      <ClassSelector value={cls?.id} onChange={choose} data-id="element-2066" data-mp="20o446fqe3u9f" />

      {cls && <div className="space-y-3" data-id="element-2067" data-mp="25o0booyaq3tq">
          <ClassPath cls={cls} data-id="element-2068" data-mp="25myue2qosl3k" />
          <ChecklistBar list={list} total={keys.length} done={done} data-id="element-2069" data-mp="25q38psxv8x7y" />
        </div>}

      <ValidationLegend data-id="element-2070" data-mp="2al8rwjzvvxsc" />

      <SebinaSection cls={cls} list={list} lookup={lookup} status={status} data-id="element-2071" data-mp="2eoondd07vs3g" />
      <MarlonSection cls={cls} list={list} lookup={lookup} status={status} data-id="element-2072" data-mp="2emua8kv6t49x" />
      <ThirdEvolutionSection cls={cls} list={list} lookup={lookup} status={status} data-id="element-2073" data-mp="gawsa8wdfwrg" />

      <Link to="/guia/alas" className="group flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[#0A1628] p-4 transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-2074" data-mp="kbp1511bzzn7">
        <span className="flex items-center gap-3" data-id="element-2075" data-mp="7zwe6au515tc">
          <FlaskConical className="h-5 w-5 text-[#D4AF37]" aria-hidden="true" data-id="element-2076" data-mp="31l6a5nel5dr" />
          <span data-id="element-2077" data-mp="2zii7r5t9mbo">
            <span className="block font-cinzel font-bold text-white" data-id="element-2078" data-mp="3v5dz3zry5w2c">Siguiente mejora: alas y capas</span>
            <span className="block text-xs text-gray-400" data-id="element-2079" data-mp="vq0i6jb8n7kv">Arma Chaos, Evo 1, Evo 2, Condor Feather y Evo 3</span>
          </span>
        </span>
        <ArrowRight className="h-4 w-4 text-gray-500 group-hover:text-[#D4AF37]" aria-hidden="true" data-id="element-2080" data-mp="qsh2rg2t0orc" />
      </Link>
    </div>;
}

/** Todas las casillas de progreso que ve una clase, para el contador. */
function progressKeys(cls: CharacterClass, lookup?: WorldLookup): string[] {
  const keys: string[] = [];
  const add = (questId: number, prefix: string, steps: string[]) => {
    const v = lookup?.quest(questId, cls);
    if (lookup && (!v || !v.eligible)) return;
    keys.push(...steps.map((_, i) => `${prefix}:paso-${i}`));
    v?.items.forEach(it => keys.push(`${prefix}:${it.objective.id}`));
    v?.kills.forEach(k => keys.push(`${prefix}:${k.objective.id}`));
  };
  if (cls.second) {
    add(0, 'sebina-0', sebina.stepsPart1);
    add(1, 'sebina-1', sebina.stepsPart2);
  }
  if (cls.marlon) add(2, 'marlon-2', marlon.steps);
  if (cls.combo) add(3, 'marlon-3', marlon.comboSteps);
  add(4, 'tercera-4', thirdEvolution.stage1.steps);
  add(5, 'tercera-5', thirdEvolution.stage2.steps);
  add(6, 'tercera-6', thirdEvolution.stage3.steps);
  return keys;
}